/* ============================================================
 * Whop checkout — VELDORO
 * ------------------------------------------------------------
 * Add-to-cart is left completely alone: native Shopify cart, drawer,
 * quantities, cart page. Only the CHECKOUT button is redirected to Whop.
 *
 * Checkout goes through the sync service, which prices the cart from Shopify
 * and mints a Whop checkout for it. That route exists for two reasons:
 *
 *   1. a cart can hold several lines or quantities above one, which a fixed
 *      per-variant link cannot represent;
 *   2. it is the only way to attach this shopper's AD ATTRIBUTION
 *      (fbp/fbc/UTMs) to the purchase. The pre-made per-variant links are
 *      shared by every buyer, so nothing buyer-specific can ride on them, and
 *      an order without those identifiers cannot be matched to a campaign by
 *      wetracked or Meta CAPI.
 *
 * If that call fails, checkout still completes: a single-unit cart falls back
 * to the pre-made link, anything else to Shopify's own checkout. Attribution
 * is lost in that case, which is why the order gets tagged whop-no-attribution.
 *
 * Debug:  sessionStorage.setItem('whop_debug','1')
 * Opt out a button:  data-whop-ignore
 * Force a button in: data-whop-checkout
 * ============================================================ */
(function () {
  'use strict';

  var W = (window.WHOP = window.WHOP || {});
  var BASE = W.checkoutBase || 'https://whop.com/checkout/';
  var BY_CURRENCY = W.checkoutsByCurrency || {};
  var CHECKOUTS = W.checkouts || {};
  var PLANS = W.plans || {};
  var CURRENCY = String(W.currency || 'USD').toUpperCase();
  var COUNTRY = String(W.country || 'US').toUpperCase();
  var CART_ENDPOINT = W.cartCheckoutUrl || null;

  var MSG = W.messages || {};
  MSG.failed = MSG.failed || 'Sorry, we could not start checkout just now. Please try again in a moment.';
  MSG.empty = MSG.empty || 'Your cart is empty.';
  W.messages = MSG;

  var TAG = '[whop]';
  function debugOn() {
    try { return sessionStorage.getItem('whop_debug') === '1'; } catch (e) { return false; }
  }
  function log() {
    if (!debugOn()) return;
    try { console.log.apply(console, [TAG].concat([].slice.call(arguments))); } catch (e) {}
  }
  function warn() {
    try { console.warn.apply(console, [TAG].concat([].slice.call(arguments))); } catch (e) {}
  }

  var SEL_CHECKOUT = [
    '[data-whop-checkout]',
    '.cart__checkout-button',
    '.cart-drawer__checkout',
    'button[name="checkout"]',
    'input[name="checkout"]',
    '#checkout',
    '#CartDrawer-Checkout'
  ].join(',');

  /* Buy Now skips the cart and would land on Shopify's checkout. */
  var SEL_BUY_NOW = [
    '.shopify-payment-button__button',
    'shopify-buy-it-now-button',
    '.product__buy-now',
    '[data-action="buy-now"]'
  ].join(',');

  var navigating = false;

  function attribution(extra) {
    try {
      if (window.WHOP_ATTRIBUTION && window.WHOP_ATTRIBUTION.collect) {
        return window.WHOP_ATTRIBUTION.collect(extra);
      }
    } catch (e) {
      warn('attribution collection failed', e);
    }
    return extra || {};
  }

  function staticTarget(variantId) {
    if (!variantId) return null;
    var key = String(variantId);
    var byCurrency = BY_CURRENCY[key];
    if (byCurrency && byCurrency[CURRENCY]) {
      return BASE + encodeURIComponent(byCurrency[CURRENCY]) + '/';
    }
    if (CHECKOUTS[key]) return BASE + encodeURIComponent(CHECKOUTS[key]) + '/';
    if (PLANS[key]) return BASE + encodeURIComponent(PLANS[key]);
    return null;
  }

  function onWhop(variantId) {
    var key = String(variantId);
    return Boolean(BY_CURRENCY[key] || CHECKOUTS[key] || PLANS[key]);
  }

  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(sel)) return el;
      el = el.parentElement || (el.parentNode && el.parentNode.host) || null;
    }
    return null;
  }

  function stop(e) {
    if (!e) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }

  function go(url, how) {
    if (navigating) return;
    navigating = true;
    log('redirecting via', how, 'to', url);
    window.location.href = url;
  }

  function nativeCheckout() {
    navigating = true;
    window.location.href = '/checkout';
  }

  function fail(message) {
    navigating = false;
    if (typeof W.onError === 'function') {
      try { W.onError(message); return; } catch (e) { warn('onError threw', e); }
    }
    warn(message);
    window.alert(message);
  }

  function busy(btn, on) {
    if (!btn || !btn.classList) return;
    if (on) {
      btn.classList.add('loading', 'button-loading');
      btn.setAttribute('aria-busy', 'true');
      if ('disabled' in btn) btn.disabled = true;
    } else {
      btn.classList.remove('loading', 'button-loading');
      btn.removeAttribute('aria-busy');
      if ('disabled' in btn) btn.disabled = false;
    }
  }

  /** Mint a Whop checkout for these lines, carrying this shopper's attribution. */
  async function mintCheckout(items, extraAttribution) {
    var res = await fetch(CART_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        country: COUNTRY,
        items: items,
        attribution: attribution(extraAttribution),
      }),
    });
    var payload = await res.json().catch(function () { return null; });
    if (!res.ok || !payload || !payload.url) {
      throw new Error('checkout rejected: ' + res.status + ' ' + JSON.stringify(payload));
    }
    return payload;
  }

  async function checkoutFromCart(btn) {
    var cart;
    try {
      var res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('cart.js ' + res.status);
      cart = await res.json();
    } catch (err) {
      warn('cart lookup failed — using Shopify checkout', err);
      return nativeCheckout();
    }

    var items = (cart && cart.items) || [];
    if (!items.length) return fail(MSG.empty);
    if (cart.currency) CURRENCY = String(cart.currency).toUpperCase();

    /* A cart holding anything not sold through Whop cannot be split. */
    if (!items.every(function (i) { return onWhop(i.variant_id); })) {
      log('cart has non-Whop items — using Shopify checkout');
      return nativeCheckout();
    }

    var lines = items.map(function (i) {
      return { variantId: String(i.variant_id), quantity: i.quantity };
    });

    busy(btn, true);
    try {
      var payload = await mintCheckout(lines, { 'shopify-cart-token': cart.token });
      go(payload.url, 'cart/' + payload.currency + ' ' + payload.total);
    } catch (err) {
      warn('cart checkout failed', err);
      busy(btn, false);
      /* Complete the sale rather than lose it. Attribution is lost here, and the
       * order is tagged whop-no-attribution so it is visible afterwards. */
      if (items.length === 1 && items[0].quantity === 1) {
        var url = staticTarget(items[0].variant_id);
        if (url) return go(url, 'fallback-static/' + CURRENCY);
      }
      nativeCheckout();
    }
  }

  function readSelection(el) {
    var form = closest(el, 'form');
    var field = form && form.querySelector('input[name="id"], select[name="id"]');
    var variantId = (field && field.value) || null;
    if (!variantId) {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      variantId = (meta && meta.selectedVariantId) || null;
    }
    var qtyEl =
      (form && form.querySelector('input[name="quantity"], .quantity__input')) ||
      document.querySelector('.quantity__input, input[name="quantity"]');
    var qty = parseInt((qtyEl && qtyEl.value) || '1', 10);
    if (!qty || qty < 1) qty = 1;
    return { variantId: variantId, qty: qty };
  }

  async function buyNow(btn, e) {
    var sel = readSelection(btn);
    if (!onWhop(sel.variantId)) return; // not ours; let Shopify handle it
    stop(e);

    busy(btn, true);
    try {
      var payload = await mintCheckout([
        { variantId: String(sel.variantId), quantity: sel.qty },
      ]);
      go(payload.url, 'buy-now/' + payload.currency);
    } catch (err) {
      warn('buy now failed', err);
      busy(btn, false);
      if (sel.qty === 1) {
        var url = staticTarget(sel.variantId);
        if (url) return go(url, 'fallback-static/' + CURRENCY);
      }
      nativeCheckout();
    }
  }

  /* Capture-phase listeners on document run before any per-button handler. */
  document.addEventListener(
    'click',
    function (e) {
      var target = e.target;
      if (!target || target.nodeType !== 1) return;
      if (closest(target, '[data-whop-ignore]')) return;

      var btn = closest(target, SEL_CHECKOUT);
      if (btn) {
        stop(e);
        checkoutFromCart(btn);
        return;
      }
      btn = closest(target, SEL_BUY_NOW);
      if (btn) buyNow(btn, e);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.matches) return;
      if (closest(form, '[data-whop-ignore]')) return;
      var action = form.getAttribute('action') || '';
      if (/\/checkout/.test(action)) {
        stop(e);
        checkoutFromCart(form.querySelector(SEL_CHECKOUT));
      }
      // /cart/add submissions are left alone: adding to cart stays native.
    },
    true
  );

  window.addEventListener('pageshow', function (e) {
    if (e.persisted) navigating = false;
  });

  log('ready —', CURRENCY, 'in', COUNTRY + '; endpoint', CART_ENDPOINT ? 'configured' : 'MISSING');
})();
